import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSinglePost, updatePost } from "../actions/postActions";
import { fetchProjectDetails } from "../actions/projectActions";
import { POST_UPDATE_RESET } from "../constants/postConstants"; // <--- add a reset const

const EditPostScreen = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoURL, setVideoURL] = useState("");
  const [modelURL, setModelURL] = useState("");
  const [walkthroughURL, setWalkthroughURL] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [postDate, setPostDate] = useState("");

  // postDetails slice for loading single post
  const postDetails = useSelector((state) => state.postDetails);
  const { loading, error, post } = postDetails;

  // postUpdate slice with distinct success key
  const postUpdate = useSelector((state) => state.postUpdate);
  const {
    loading: updateLoading,
    error: updateError,
    postUpdateSuccess, // <--- rename from 'success'
    updatedPost,
  } = postUpdate;

  useEffect(() => {
    if (!post || post._id !== postId) {
      dispatch(getSinglePost(postId));
    } else {
      setTitle(post.title);
      setDescription(post.description);
      setVideoURL(post.videoURL || "");
      setModelURL(post.modelURL || "");
      setWalkthroughURL(post.walkthroughURL || "");
      setSnapshots(post.snapshots || []);
      const dateString = new Date(post.createdAt).toLocaleDateString(
        undefined,
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      );
      setPostDate(dateString);
    }
  }, [dispatch, postId, post]);

  // If update success, re-fetch project or navigate, then reset
  useEffect(() => {
    if (postUpdateSuccess) {
      if (post?.project) {
        dispatch(fetchProjectDetails(post.project));
      }
      // navigate back or /posts/:id
      navigate(-1);

      // RESET so next update isn't overshadowed
      dispatch({ type: POST_UPDATE_RESET });
    }
  }, [postUpdateSuccess, dispatch, navigate, post]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedData = {
      id: postId,
      title,
      description,
      videoURL,
      modelURL,
      walkthroughURL,
      snapshots,
    };
    dispatch(updatePost(updatedData));
  };

  // 4) Adding snapshot URLs via prompt (for demonstration)
  const handleAddSnapshotURL = () => {
    const url = prompt("Enter new snapshot URL:");
    if (url) {
      setSnapshots((prev) => [...prev, url]);
    }
  };

  // 5) Removing snapshot
  const removeSnapshot = (index) => {
    setSnapshots((prev) => prev.filter((_, i) => i !== index));
  };

  // 6) Handling multiple file input (local preview approach)
  // This won't actually upload the files to the server.
  // In production, you'd typically do an S3 or Cloudinary upload.
  const handleSnapshotFiles = (e) => {
    const files = Array.from(e.target.files);
    // For demonstration, create local URLs. Real app: upload to server, get final URL.
    const newUrls = files.map((file) => URL.createObjectURL(file));
    setSnapshots((prev) => [...prev, ...newUrls]);
  };

  return (
    <div>
      <h2>
        Edit Post - Date: {postDate} <br />
        ID: {postId}
      </h2>

      {/* Show errors/loading */}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {loading && <p>Loading post details...</p>}
      {updateError && <p style={{ color: "red" }}>{updateError}</p>}
      {updateLoading && <p>Updating post...</p>}

      {/* Only show form if we have the post data */}
      {post && (
        <form onSubmit={handleSubmit}>
          <div>
            <label>Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div>
            <label>Description:</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div>
            <label>Video URL:</label>
            <input
              type="text"
              value={videoURL}
              onChange={(e) => setVideoURL(e.target.value)}
            />
          </div>

          <div>
            <label>3D Model URL:</label>
            <input
              type="text"
              value={modelURL}
              onChange={(e) => setModelURL(e.target.value)}
            />
          </div>

          <div>
            <label>360 Walkthrough URL:</label>
            <input
              type="text"
              value={walkthroughURL}
              onChange={(e) => setWalkthroughURL(e.target.value)}
            />
          </div>

          <div>
            <label>Snapshots:</label>
            <ul>
              {snapshots.map((snap, idx) => (
                <li key={idx}>
                  {snap}{" "}
                  <button type="button" onClick={() => removeSnapshot(idx)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            {/* 2 ways to add snapshots */}
            <button type="button" onClick={handleAddSnapshotURL}>
              Add Snapshot URL
            </button>
            <br />
            <label>
              Or Upload Snapshot Files:
              <input type="file" multiple onChange={handleSnapshotFiles} />
            </label>
          </div>

          <button type="submit">Update Post</button>
        </form>
      )}
    </div>
  );
};

export default EditPostScreen;
